import type { TenantAccess } from '@konyaxl/shared';
import { parseKodCiftleri } from '@konyaxl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiRequestError, api } from '../core/api.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
}

interface Durum {
  toplam: number;
  sonYukleme: string | null;
  tamamlandi: boolean;
}

interface Sonuc {
  yazilan: number;
  atlanan: number;
  atlananlar: Array<{ satir: number; sebep: string }>;
  toplam: number;
}

/**
 * Referans eşleştirme listesini elle yükleme.
 *
 * Liste başka bir sistemde üretiliyor; oradan kopyalanıp buraya
 * yapıştırılıyor. Sunucudan sunucuya otomatik aktarım da var ama araya
 * başka bir uygulamanın kurulumu girdiği için bu ekran, listeyi kimseye
 * bağımlı olmadan güncelleyebilmeyi sağlıyor.
 */
export function ReferansPage({ tenant, onBack }: Props) {
  const [metin, setMetin] = useState('');
  const [degistir, setDegistir] = useState(true);
  const [durum, setDurum] = useState<Durum | null>(null);
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [atlananOnizleme, setAtlananOnizleme] = useState<
    Array<{ satir: number; sebep: string }>
  >([]);
  const [mesgul, setMesgul] = useState(false);
  const dosyaRef = useRef<HTMLInputElement>(null);

  /** Sunucuyla aynı ayrıştırıcı — ekrandaki sayı yazılacak sayıdır. */
  const onizleme = useMemo(() => parseKodCiftleri(metin), [metin]);

  const durumYukle = useCallback(async () => {
    try {
      setDurum(await api.get<Durum>(`/${tenant.slug}/reference/stats`));
    } catch {
      // Durum gösterilemese de yükleme yapılabilir.
    }
  }, [tenant.slug]);

  useEffect(() => {
    void durumYukle();
  }, [durumYukle]);

  async function dosyaSecildi(dosya: File) {
    setHata(null);
    try {
      setMetin(await dosya.text());
    } catch {
      setHata('Dosya okunamadı');
    }
  }

  async function yukle() {
    if (onizleme.ciftler.length === 0) return;
    setMesgul(true);
    setHata(null);
    setSonuc(null);
    setAtlananOnizleme([]);

    try {
      const res = await api.post<Sonuc>(`/${tenant.slug}/reference`, {
        text: metin,
        replace: degistir,
      });
      setSonuc(res);
      setMetin('');
      await durumYukle();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setHata(err.message);
        const govde = err.body as { atlananlar?: Array<{ satir: number; sebep: string }> };
        setAtlananOnizleme(govde?.atlananlar ?? []);
      } else {
        setHata('Liste yüklenemedi');
      }
    } finally {
      setMesgul(false);
    }
  }

  const yuklemeMetni = durum?.sonYukleme
    ? new Date(durum.sonYukleme).toLocaleString('tr-TR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : null;

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Referans listesi</h1>
        <div className="ust-bar-sag">
          {durum && durum.toplam > 0 && <span>{durum.toplam} eşleştirme</span>}
        </div>
      </header>

      <main className="icerik">
        {durum && (
          <div className="ozet-kartlar">
            <div className="ozet-kart">
              <span className="ozet-sayi">{durum.toplam}</span>
              <span className="ozet-etiket">Kayıtlı eşleştirme</span>
            </div>
            <div className={`ozet-kart ${durum.tamamlandi ? '' : 'kirmizi'}`}>
              <span className="ozet-sayi" style={{ fontSize: 18 }}>
                {yuklemeMetni ?? '—'}
              </span>
              <span className="ozet-etiket">
                {durum.tamamlandi ? 'Son yükleme' : 'Yükleme yarım kaldı'}
              </span>
            </div>
          </div>
        )}

        {hata && <div className="hata-kutu">{hata}</div>}

        {atlananOnizleme.length > 0 && (
          <div className="hata-kutu">
            <strong>Atlanan satırlar:</strong>
            <ul className="atlanan-liste">
              {atlananOnizleme.slice(0, 10).map((a) => (
                <li key={a.satir}>
                  satır {a.satir}: {a.sebep}
                </li>
              ))}
            </ul>
          </div>
        )}

        {sonuc && (
          <div className="basari-kutu">
            <strong>{sonuc.yazilan} eşleştirme yüklendi.</strong>
            {sonuc.atlanan > 0 && ` ${sonuc.atlanan} satır atlandı.`} Listede toplam{' '}
            {sonuc.toplam} eşleştirme var.
            {sonuc.atlananlar.length > 0 && (
              <ul className="atlanan-liste">
                {sonuc.atlananlar.slice(0, 10).map((a) => (
                  <li key={a.satir}>
                    satır {a.satir}: {a.sebep}
                  </li>
                ))}
                {sonuc.atlanan > 10 && <li>… ve {sonuc.atlanan - 10} satır daha</li>}
              </ul>
            )}
          </div>
        )}

        <section className="bolum">
          <h2 className="bolum-baslik">Liste yükle</h2>
          <p className="bolum-aciklama">
            İki sütunu kopyalayıp yapıştırın: solda{' '}
            <strong>{tenant.scanRules.a.label.toLowerCase()}</strong>, sağda{' '}
            <strong>{tenant.scanRules.b.label.toLowerCase()}</strong>. Excel'den
            kopyalanan hücreler doğrudan çalışır; virgül ve noktalı virgül de
            ayırıcı olarak kabul edilir. Başlık satırı varsa atlanır.
          </p>

          <textarea
            className="liste-alan"
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            placeholder={'LMI10000001\tSIP-100001\nLMI10000002\tSIP-100002'}
            rows={14}
            spellCheck={false}
            autoFocus
          />

          <div className="liste-arac">
            <span className="onizleme-bilgi">
              {onizleme.ciftler.length > 0
                ? `${onizleme.ciftler.length} eşleştirme algılandı` +
                  (onizleme.atlananlar.length > 0
                    ? ` · ${onizleme.atlananlar.length} satır atlanacak`
                    : '')
                : 'Henüz veri girilmedi'}
            </span>

            <button
              className="btn-excel"
              onClick={() => dosyaRef.current?.click()}
              disabled={mesgul}
            >
              Dosyadan al (.csv / .txt)
            </button>

            <label className="filtre-onay">
              <input
                type="checkbox"
                checked={degistir}
                onChange={(e) => setDegistir(e.target.checked)}
              />
              Mevcut listenin yerine yaz
            </label>

            <button
              className="btn-birincil dar"
              onClick={() => void yukle()}
              disabled={mesgul || onizleme.ciftler.length === 0}
            >
              {mesgul ? 'Yükleniyor…' : 'Listeyi yükle'}
            </button>
          </div>

          {!degistir && (
            <p className="bolum-aciklama">
              Ekleme modunda: mevcut liste korunur, aynı barkod gelirse sipariş
              kodu güncellenir.
            </p>
          )}

          <input
            ref={dosyaRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            hidden
            onChange={(e) => {
              const dosya = e.target.files?.[0];
              if (dosya) void dosyaSecildi(dosya);
              e.target.value = '';
            }}
          />
        </section>

        {onizleme.ciftler.length > 0 && (
          <section className="bolum">
            <h2 className="bolum-baslik">Önizleme (ilk 10)</h2>
            <table className="kayit-tablo">
              <thead>
                <tr>
                  <th>{tenant.scanRules.a.label}</th>
                  <th>{tenant.scanRules.b.label}</th>
                </tr>
              </thead>
              <tbody>
                {onizleme.ciftler.slice(0, 10).map((c) => (
                  <tr key={c.barkod}>
                    <td className="kod">{c.barkod}</td>
                    <td className="kod">{c.siparis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>
    </>
  );
}
